/* istanbul ignore file */
/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { getInfCLogger } from '@infodata-group/inf-commons-core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { ModelProperty } from './model-property';
import { DataModel } from './data-model';
import { DateTime } from '../model/date-time';
import { GroupEntities } from '../model/entity/group-entities';
import { Selectable } from '../interface/selectable';
import { Time } from '../types/time.type';

const logger = getInfCLogger('DataConverter');

/**
 * @deprecated The method should not be used
 * Replaced by @commons-utils
 */
export class DataConverter {
  private static pipe = new DatePipe('fr');
  /**
   * Convert string format dd/mm/yyyy to javascript date object.
   * if date isn't in correct format, return null
   * @param str date on dd/mm/yyyy format
   */
  public static convertStrToDate(str: string): Date {
    if (str) {
      const array = str.split('/');
      if (array.length === 3) {
        return new Date(
          parseInt(array[2], 10),
          parseInt(array[1], 10) - 1,
          parseInt(array[0], 10),
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  /**
   * Convert Js date object to a date string format dd/mm/yyyy
   * @param date js Date Object
   */
  public static convertDateToString(date: Date): string {
    return DataConverter.pipe.transform(date, 'dd/MM/yyyy');
  }

  /**
   * Convert Js date object to a date string format dd/mm/yyyy
   * @param date js Date Object
   * @param second set true to display second
   */
  public static convertDateToTimeString(date: Date, second?: boolean): string {
    if (second) {
      return DataConverter.pipe.transform(date, 'HH:mm:ss');
    } else {
      return DataConverter.pipe.transform(date, 'HH:mm');
    }
  }

  /**
   * Convert Js date object to a date string format dd/mm/yyyy
   * @param date js Date Object
   * @param second display seconds
   */
  public static convertDateToDateTimeString(
    date: Date,
    second?: boolean,
  ): string {
    if (second) {
      return DataConverter.pipe.transform(date, 'dd/MM/yyyy HH:mm:ss');
    } else {
      return DataConverter.pipe.transform(date, 'dd/MM/yyyy HH:mm');
    }
  }

  /**
   * Convert Js date object to a date string format dd/mm/yyyy
   * @param date js Date Object
   */
  public static convertDateToISOString(date: Date): string {
    return DataConverter.pipe.transform(date, 'yyyy-MM-dd');
  }

  /**
   * Convert string date format hh:mm:ss to time in millis
   * @param str time format hh:mm:ss
   */
  public static convertStrToTimeInMillis(str: string): number {
    const array = str.split(':');
    return (
      parseInt(array[0], 10) * 3600 * 1000 +
      parseInt(array[1], 10) * 60 * 1000 +
      (array.length === 3 ? parseInt(array[2], 10) * 1000 : 0)
    );
  }

  /**
   * Convert dateTime string to javascript dateObject
   * @param date dd/mm/yyyy format date string
   * @param time hh:mm:ss format time string
   */
  public static convertStrToDateTime(date: string, time: string): Date {
    try {
      return new Date(
        this.convertStrToDate(date).getTime() +
          this.convertStrToTimeInMillis(time),
      );
    } catch (e) {
      logger.error(`Impossible to convert "${date} ${time}" to a date`, e);
      return null;
    }
  }

  /**
   * Convert decimal System number to Hexa string
   * @param n number or string number
   * @return hexadecimal String number
   */
  public static convertDecimalNumberToHexa(n: number | string): string {
    let hex = Number(n).toString(16);
    if (hex.length < 2) {
      hex = '0' + hex;
    }
    return hex;
  }

  public static convertMilliToValue(n: number, type: 'day'): number {
    if (type === 'day') {
      const diffTime = Math.abs(n);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    } else {
      return n;
    }
  }

  /**
   * Convert string to normalize string without accent
   * @param str
   * @param lowerCase to return lower-case string
   */
  public static normalize(str: string, lowerCase = false): string {
    const result = (str + '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (lowerCase) {
      return result.toLowerCase();
    } else {
      return result;
    }
  }

  static convertStrToTime(defaultTime: string): Time {
    const split = defaultTime.split(':');
    if (split.length > 1) {
      const h = parseInt(split[0], 10);
      const m = parseInt(split[1], 10);
      return { hours: h, minutes: m };
    }
    return undefined;
  }

  static convertTimeToString(time: Time) {
    const pipe = new DecimalPipe('fr');
    return `${pipe.transform(time.hours, '2.0-0')}:${pipe.transform(time.minutes, '2.0-0')}`;
  }

  public static serializeData(model: DataModel, o: any) {
    const data: any = {};
    // tslint:disable-next-line:forin
    for (const field in o) {
      const prop = model.find(field);
      const v = o[field];
      if (v instanceof Array) {
        data[prop.id] = v.map((x) => this.getValue(prop, x));
      } else {
        data[prop.id] = this.getValue(prop, v);
      }
    }
    return data;
  }

  public static getValue(prop: ModelProperty, v: any): any {
    if (prop && v) {
      const key = prop.getPrimaryKey();
      const labelKey = prop.getLabelKey() ? prop.getLabelKey()[0] : null;
      if (key) {
        const d = {};
        if (v.getId) {
          d[key] = v.getId();
          if (labelKey) {
            d[labelKey] = v.getLabel();
          }
        } else {
          d[key] = v;
        }
        return d;
      } else {
        if (v.getId) {
          return v.getId();
        }
        if (v instanceof Date) {
          return DataConverter.convertDateToString(v);
        }
        if (v instanceof DateTime) {
          return v.serialize();
        }
        return v;
      }
    }
  }

  public static convertToGroup<T extends Selectable>(
    entities: T[],
    groupField: string,
  ): GroupEntities<T>[] {
    const groups: GroupEntities<T>[] = [];
    for (const e of entities) {
      const name = e[groupField];
      let group = groups.find((gr) => gr.id === name);
      if (!group) {
        group = new GroupEntities<any>(name, name, [e]);
        groups.push(group);
      } else {
        group.push(e);
      }
    }
    return groups;
  }

  public compare(comparant: any, comparant2: any): number {
    if (typeof comparant !== typeof comparant2) {
      console.warn('not the same type');
      return 0;
    }
    if (typeof comparant === 'string') {
      return comparant.localeCompare(comparant2);
    }
    if (typeof comparant2 === 'number') {
      return comparant === comparant2 ? 0 : -1;
    }
    if (comparant instanceof Object) {
      return comparant.toString().localeCompare(comparant2.toString());
    }
    if (comparant instanceof Array) {
      return comparant.join(',').localeCompare(comparant2.join(','));
    }
    return 0;
  }
}
